/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/* ============================================
   PRODUCT DESCRIPTION ACCORDION/DROPDOWN
   Converts h2/h3/strong headings in product descriptions
   into collapsible accordion sections (two-column)
   Removes finance references from all listings
   ============================================ */
(function() {
  "use strict";

  var style = document.createElement("style");
  style.textContent = [
    ".pba-accordion-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 0 24px; }",
    "@media (max-width: 768px) { .pba-accordion-columns { grid-template-columns: 1fr; } }",
    ".pba-accordion-item { border-bottom: 1px solid #e0e0e0; }",
    ".pba-accordion-col > .pba-accordion-item:first-child { border-top: 1px solid #e0e0e0; }",
    ".pba-accordion-toggle { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 18px 0; background: none; border: none; cursor: pointer; font-size: 1.1em; font-weight: 600; text-align: left; color: inherit; font-family: inherit; line-height: 1.3; }",
    ".pba-accordion-toggle:hover { opacity: 0.75; }",
    ".pba-accordion-chevron { flex-shrink: 0; margin-left: 12px; transition: transform 0.3s ease; }",
    ".pba-accordion-open .pba-accordion-chevron { transform: rotate(180deg); }",
    ".pba-accordion-panel { display: none; padding: 0 0 20px 0; }",
    ".pba-accordion-panel > *:first-child { margin-top: 0; }",
    ".pba-accordion-panel > *:last-child { margin-bottom: 0; }"
  ].join("\n");
  document.head.appendChild(style);

  var FINANCE_KEYWORDS = ["finance this product", "finance your purchase", "click here to finance"];

  function isFinanceNode(node) {
    if (node.nodeType !== 1) return false;
    var text = node.textContent.toLowerCase().trim();
    for (var i = 0; i < FINANCE_KEYWORDS.length; i++) {
      if (text.indexOf(FINANCE_KEYWORDS[i]) > -1) return true;
    }
    var links = node.querySelectorAll("a");
    for (var j = 0; j < links.length; j++) {
      var href = (links[j].getAttribute("href") || "").toLowerCase();
      if (href.indexOf("finance") > -1 || href.indexOf("klarna") > -1 || href.indexOf("clearpay") > -1) return true;
    }
    return false;
  }

  function removeFinanceNodes(container) {
    var toRemove = [];
    Array.from(container.childNodes).forEach(function(node) {
      if (isFinanceNode(node)) toRemove.push(node);
    });
    toRemove.forEach(function(node) { node.remove(); });
    return toRemove.length;
  }

  function isStrongHeading(node) {
    if (node.nodeType !== 1 || node.tagName !== "P") return false;
    var strong = node.querySelector("strong");
    if (!strong) return false;
    var strongText = strong.textContent.trim();
    var fullText = node.textContent.trim();
    return strongText === fullText && strongText.length > 2;
  }

  function isFinanceHeading(node, headingType) {
    var text = "";
    if (headingType === "STRONG") {
      var s = node.querySelector("strong");
      text = s ? s.textContent.toLowerCase().trim() : "";
    } else {
      text = node.textContent.toLowerCase().trim();
    }
    for (var i = 0; i < FINANCE_KEYWORDS.length; i++) {
      if (text.indexOf(FINANCE_KEYWORDS[i]) > -1) return true;
    }
    return false;
  }

  function getHeadingText(node, headingType) {
    if (headingType === "STRONG") {
      return node.querySelector("strong").textContent.replace(/:$/, "").trim();
    }
    return node.textContent.replace(/:$/, "").trim();
  }

  function initProductAccordion() {
    var rteContainers = document.querySelectorAll(".product-tabs__tab-item-content.rte");
    rteContainers.forEach(function(rte) {
      if (rte.dataset.accordionInit) return;

      var container = rte;
      var headingType = null;

      removeFinanceNodes(container);
      var innerDivs = container.querySelectorAll(":scope > div");
      for (var dd = 0; dd < innerDivs.length; dd++) {
        removeFinanceNodes(innerDivs[dd]);
      }

      var h2s = container.querySelectorAll(":scope > h2");
      if (h2s.length >= 2) { headingType = "H2"; }
      if (!headingType) {
        var h3s = container.querySelectorAll(":scope > h3");
        if (h3s.length >= 2) { headingType = "H3"; }
      }
      if (!headingType) {
        var strongCount = 0;
        Array.from(container.children).forEach(function(child) {
          if (isStrongHeading(child)) strongCount++;
        });
        if (strongCount >= 2) { headingType = "STRONG"; }
      }
      if (!headingType) {
        for (var d = 0; d < innerDivs.length; d++) {
          var ih2 = innerDivs[d].querySelectorAll(":scope > h2");
          if (ih2.length >= 2) { container = innerDivs[d]; headingType = "H2"; break; }
          var ih3 = innerDivs[d].querySelectorAll(":scope > h3");
          if (ih3.length >= 2) { container = innerDivs[d]; headingType = "H3"; break; }
          var iStrong = 0;
          Array.from(innerDivs[d].children).forEach(function(child) {
            if (isStrongHeading(child)) iStrong++;
          });
          if (iStrong >= 2) { container = innerDivs[d]; headingType = "STRONG"; break; }
        }
      }
      if (!headingType) { rte.dataset.accordionInit = "true"; return; }

      rte.dataset.accordionInit = "true";

      var sections = [];
      var currentSection = null;
      Array.from(container.childNodes).forEach(function(node) {
        var isHeading = false;
        if (headingType === "STRONG") {
          isHeading = isStrongHeading(node);
        } else {
          isHeading = (node.nodeType === 1 && node.tagName === headingType);
        }
        if (isHeading) {
          if (currentSection) sections.push(currentSection);
          currentSection = { heading: node, content: [] };
        } else if (currentSection) {
          currentSection.content.push(node);
        }
      });
      if (currentSection) sections.push(currentSection);

      sections = sections.filter(function(sec) {
        return !isFinanceHeading(sec.heading, headingType);
      });

      if (sections.length < 2) return;

      var midpoint = Math.ceil(sections.length / 2);
      var grid = document.createElement("div");
      grid.className = "pba-accordion-columns";

      function buildColumn(secs) {
        var col = document.createElement("div");
        col.className = "pba-accordion-col";
        secs.forEach(function(section) {
          var wrapper = document.createElement("div");
          wrapper.className = "pba-accordion-item";
          var toggle = document.createElement("button");
          toggle.className = "pba-accordion-toggle";
          toggle.setAttribute("type", "button");
          toggle.setAttribute("aria-expanded", "false");
          toggle.innerHTML = '<span class="pba-accordion-title">' + getHeadingText(section.heading, headingType) + "</span>" +
            '<svg class="pba-accordion-chevron" width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M7 9.5L1.5 4l1-1L7 7.5 11.5 3l1 1z" fill="currentColor"/></svg>';
          var panel = document.createElement("div");
          panel.className = "pba-accordion-panel";
          section.content.forEach(function(node) { panel.appendChild(node); });
          toggle.addEventListener("click", function() {
            var isOpen = wrapper.classList.contains("pba-accordion-open");
            grid.querySelectorAll(".pba-accordion-item").forEach(function(item) {
              item.classList.remove("pba-accordion-open");
              item.querySelector(".pba-accordion-toggle").setAttribute("aria-expanded", "false");
              item.querySelector(".pba-accordion-panel").style.display = "none";
            });
            if (!isOpen) {
              wrapper.classList.add("pba-accordion-open");
              toggle.setAttribute("aria-expanded", "true");
              panel.style.display = "block";
            }
          });
          wrapper.appendChild(toggle);
          wrapper.appendChild(panel);
          col.appendChild(wrapper);
          section.heading.remove();
        });
        return col;
      }

      grid.appendChild(buildColumn(sections.slice(0, midpoint)));
      grid.appendChild(buildColumn(sections.slice(midpoint)));

      while (container.firstChild) container.removeChild(container.firstChild);
      container.appendChild(grid);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initProductAccordion);
  } else {
    initProductAccordion();
  }

  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(m) { if (m.addedNodes.length) initProductAccordion(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
/* ============================================
   COLLAPSIBLE QUOTE SECTION
   Black bar with white text, positioned under
   the product description accordion columns
   ============================================ */
(function() {
  "use strict";

  var style = document.createElement("style");
  style.textContent = [
    ".pba-quote-bar { background: #2029DB; }",
    ".pba-quote-toggle { display: flex; align-items: center; justify-content: space-between; width: 100%; max-width: 1200px; margin: 0 auto; padding: 22px 24px; background: none; border: none; cursor: pointer; font-family: inherit; text-align: left; }",
    ".pba-quote-toggle:hover { opacity: 0.85; }",
    ".pba-quote-toggle-title { font-size: 1.4em; font-weight: 700; color: #fff; letter-spacing: 0.02em; text-transform: uppercase; }",
    ".pba-quote-chevron { flex-shrink: 0; margin-left: 12px; transition: transform 0.3s ease; color: #fff; }",
    ".pba-quote-bar.pba-quote-open .pba-quote-chevron { transform: rotate(180deg); }",
    ".pba-quote-content { display: none; overflow: hidden; background: #fff; }",
    ".pba-quote-bar.pba-quote-open .pba-quote-content { display: block; }"
  ].join("\n");
  document.head.appendChild(style);

  function initQuoteCollapse() {
    var shopifySection = document.querySelector(".shopify-section--contact-form");
    if (!shopifySection || shopifySection.dataset.quoteInit) return;
    var section = shopifySection.querySelector("section");
    if (!section) return;
    var colorWrapper = section.querySelector(".section__color-wrapper");
    if (!colorWrapper) return;

    shopifySection.dataset.quoteInit = "true";

    /* Move quote section to right after product content */
    var productContentSection = document.querySelector(".shopify-section--product-content");
    if (productContentSection) {
      productContentSection.parentNode.insertBefore(shopifySection, productContentSection.nextSibling);
    }

    var bar = document.createElement("div");
    bar.className = "pba-quote-bar";

    var toggle = document.createElement("button");
    toggle.className = "pba-quote-toggle";
    toggle.setAttribute("type", "button");
    toggle.setAttribute("aria-expanded", "false");
    toggle.innerHTML = '<span class="pba-quote-toggle-title">Request a Custom Quote</span>' +
      '<svg class="pba-quote-chevron" width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M7 9.5L1.5 4l1-1L7 7.5 11.5 3l1 1z" fill="currentColor"/></svg>';

    var content = document.createElement("div");
    content.className = "pba-quote-content";
    content.appendChild(colorWrapper);

    var origHeading = colorWrapper.querySelector("h1.heading, h2.heading");
    if (origHeading) origHeading.style.display = "none";
    var allP = colorWrapper.querySelectorAll("p");
    for (var i = 0; i < allP.length; i++) {
      if (allP[i].textContent.indexOf("additional requirements") > -1) {
        allP[i].style.display = "none";
        break;
      }
    }

    bar.appendChild(toggle);
    bar.appendChild(content);
    section.innerHTML = "";
    section.appendChild(bar);

    toggle.addEventListener("click", function() {
      var isOpen = bar.classList.contains("pba-quote-open");
      if (isOpen) {
        bar.classList.remove("pba-quote-open");
        toggle.setAttribute("aria-expanded", "false");
      } else {
        bar.classList.add("pba-quote-open");
        toggle.setAttribute("aria-expanded", "true");
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initQuoteCollapse);
  } else {
    initQuoteCollapse();
  }

  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(m) { if (m.addedNodes.length) initQuoteCollapse(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();